import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import VolumetricBudgetMeter from '../VolumetricBudgetMeter';
import {
  Package,
  Truck,
  RefreshCw,
  Printer,
  Download,
  ExternalLink,
  MapPin,
  Weight,
  Ruler,
  Shield,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  Box,
  Calendar,
  Search,
  X,
  IndianRupee,
  Phone,
  Mail,
  User,
  MapPinned,
  FileText,
  ChevronDown
} from 'lucide-react';

const WEIGHT_UNITS = [
  { value: 'g', label: 'grams (g)', toKg: v => v / 1000 },
  { value: 'kg', label: 'kg', toKg: v => v },
  { value: 'lbs', label: 'pounds (lbs)', toKg: v => v * 0.453592 },
];

const DelhiveryShipping = ({ orderId, orderData, onShipmentCreated, onClose }) => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [step, setStep] = useState(1); // 1: Check serviceability, 2: Enter details, 3: Confirm & Ship
  const [loading, setLoading] = useState(false);
  const [serviceability, setServiceability] = useState(null);
  const [rateInfo, setRateInfo] = useState(null);
  const [shipmentResult, setShipmentResult] = useState(null);
  const [weightUnit, setWeightUnit] = useState('g');
  const [insuranceOptIn, setInsuranceOptIn] = useState(false);
  
  // Pre-fill weight/dimensions from order's product listing data
  const getDefaultWeight = () => {
    const items = orderData?.items || [];
    for (const item of items) {
      const w = item.weight || item.weight_kg || item.data?.weight;
      if (w && w > 0) return { value: w < 1 ? w * 1000 : w, unit: w < 1 ? 'g' : 'kg' };
    }
    return { value: 500, unit: 'g' };
  };
  
  const getDefaultDimensions = () => {
    const items = orderData?.items || [];
    for (const item of items) {
      const d = item.dimensions || item.data?.dimensions || {};
      if (d.length && d.width && d.height) return d;
    }
    return { length: 16, width: 12, height: 10 };
  };
  
  const defaultWeight = getDefaultWeight();
  const defaultDims = getDefaultDimensions();
  
  // Form data
  const [formData, setFormData] = useState({
    consignee_name: orderData?.shipping_address?.name || orderData?.customer?.name || '',
    consignee_phone: orderData?.shipping_address?.phone || orderData?.customer?.phone || '',
    consignee_email: orderData?.customer?.email || '',
    // address_line1 is the canonical field in vendor_locations; address is the normalized view key
    consignee_address: orderData?.shipping_address?.address || '',
    consignee_city: orderData?.shipping_address?.city || '',
    consignee_state: orderData?.shipping_address?.state || '',
    consignee_pincode: orderData?.shipping_address?.zip || orderData?.shipping_address?.pincode || '',
    // Leave blank — backend auto-resolves from vendor's registered Delhivery warehouse
    pickup_location: '',
    payment_mode: orderData?.payment?.method === 'COD' ? 'COD' : 'Prepaid',
    order_value: orderData?.pricing?.total || orderData?.total_price || 0,
    product_description: orderData?.items?.map(i => i.title || i.name).join(', ') || 'Products',
    weight: defaultWeight.value,
    length: defaultDims.length || 20,
    width: defaultDims.width || 15,
    height: defaultDims.height || 10,
    cod_amount: orderData?.payment?.method === 'COD' ? (orderData?.pricing?.total || orderData?.total_price || 0) : 0
  });

  // Initialize weight unit from default
  useState(() => { setWeightUnit(defaultWeight.unit); });
  
  // Get weight in kg for API calls
  const getWeightInKg = () => {
    const converter = WEIGHT_UNITS.find(u => u.value === weightUnit);
    return converter ? Math.max(converter.toKg(formData.weight), 0.01) : formData.weight;
  };

  // Check pincode serviceability
  const checkServiceability = async () => {
    if (!formData.consignee_pincode || formData.consignee_pincode.length !== 6) {
      toast({
        title: 'Invalid Pincode',
        description: 'Please enter a valid 6-digit pincode',
        variant: 'destructive'
      });
      return;
    }
    
    setLoading(true);
    try {
      const data = await apiCall(`/api/delhivery/serviceability/${formData.consignee_pincode}`);
      setServiceability(data);
      
      if (data.serviceable) {
        // Auto-fill city and state if available
        if (data.city) setFormData(prev => ({ ...prev, consignee_city: data.city }));
        if (data.state) setFormData(prev => ({ ...prev, consignee_state: data.state }));
        
        toast({
          title: '✅ Pincode Serviceable',
          description: `${data.city || 'Location'} is serviceable by Delhivery`
        });
        setStep(2);
      } else {
        toast({
          title: '❌ Not Serviceable',
          description: 'This pincode is not serviceable by Delhivery',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to check serviceability',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate shipping rate
  const calculateRate = async () => {
    const weightKg = getWeightInKg();
    if (!weightKg || weightKg <= 0) {
      toast({
        title: 'Invalid Weight',
        description: 'Please enter a valid weight',
        variant: 'destructive'
      });
      return;
    }
    
    setLoading(true);
    setRateInfo(null); // Clear stale results before recalculating
    try {
      // Fetch vendor's pickup location pincode
      let originPin = '400059'; // Platform warehouse fallback
      try {
        const locations = await apiCall('/api/shipping/vendor/pickup-locations');
        const primary = (locations || []).find(l => l.is_primary && l.is_active) || (locations || [])[0];
        if (primary?.postal_code || primary?.pincode) {
          originPin = primary.postal_code || primary.pincode;
        }
      } catch (e) { /* fallback to platform warehouse pin */ }
      
      const data = await apiCall('/api/delhivery/rates', {
        method: 'POST',
        body: JSON.stringify({
          origin_pincode: originPin,
          destination_pincode: formData.consignee_pincode,
          weight: weightKg,
          order_value: parseFloat(formData.order_value),
          payment_mode: formData.payment_mode,
          length: parseFloat(formData.length),
          width: parseFloat(formData.width),
          height: parseFloat(formData.height)
        })
      });
      
      setRateInfo(data);
      setStep(3);
      
      toast({
        title: 'Rate Calculated',
        description: 'Review shipping details and confirm'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to calculate rate',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Create shipment
  const createShipment = async () => {
    setLoading(true);
    try {
      const shipmentData = {
        order_id: orderId,
        consignee_name: formData.consignee_name,
        consignee_phone: formData.consignee_phone,
        consignee_email: formData.consignee_email || undefined,
        consignee_address: formData.consignee_address,
        consignee_city: formData.consignee_city,
        consignee_state: formData.consignee_state,
        consignee_pincode: formData.consignee_pincode,
        pickup_location: formData.pickup_location,
        payment_mode: formData.payment_mode,
        order_value: parseFloat(formData.order_value),
        product_description: formData.product_description,
        weight: getWeightInKg(),
        length: parseFloat(formData.length),
        width: parseFloat(formData.width),
        height: parseFloat(formData.height),
        cod_amount: formData.payment_mode === 'COD' ? parseFloat(formData.cod_amount) : 0
      };
      
      const result = await apiCall('/api/delhivery/shipments', {
        method: 'POST',
        body: JSON.stringify(shipmentData)
      });
      
      setShipmentResult(result);
      
      toast({
        title: '🎉 Shipment Created!',
        description: `AWB: ${result.awb}`
      });
      
      if (onShipmentCreated) {
        onShipmentCreated(result);
      }
      
    } catch (error) {
      toast({
        title: 'Error Creating Shipment',
        description: error.message || 'Failed to create shipment',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Download label
  const downloadLabel = async () => {
    if (!shipmentResult?.awb) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/delhivery/shipments/${orderId}/label`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to download label');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${orderId}_shipping_label.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Label Downloaded' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download label',
        variant: 'destructive'
      });
    }
  };

  // Render Step 1: Check Serviceability
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <MapPin className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold">Check Delivery Serviceability</h3>
        <p className="text-gray-500 text-sm">Enter the delivery pincode to check if Delhivery can deliver</p>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Delivery Pincode *</Label>
          <div className="flex gap-2">
            <Input
              type="text"
              maxLength={6}
              placeholder="Enter 6-digit pincode"
              value={formData.consignee_pincode}
              onChange={(e) => setFormData({ ...formData, consignee_pincode: e.target.value.replace(/\D/g, '') })}
              className="flex-1 text-lg tracking-wider"
              data-testid="delhivery-pincode-input"
            />
            <Button 
              onClick={checkServiceability} 
              disabled={loading || formData.consignee_pincode.length !== 6}
              data-testid="check-serviceability-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Check
            </Button>
          </div>
        </div>
        
        {serviceability && !serviceability.serviceable && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Pincode Not Serviceable</span>
            </div>
            <p className="text-red-600 text-sm mt-1">
              Delhivery does not deliver to this pincode. Please use an alternative shipping method.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Render Step 2: Enter Details
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Shipment Details</h3>
          <p className="text-gray-500 text-sm">Enter package and recipient details</p>
        </div>
        {serviceability && (
          <div className="flex gap-2">
            {serviceability.prepaid && <Badge className="bg-green-100 text-green-700">Prepaid ✓</Badge>}
            {serviceability.cod && <Badge className="bg-blue-100 text-blue-700">COD ✓</Badge>}
          </div>
        )}
      </div>
      
      {/* Recipient Details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="h-4 w-4" />
            Recipient Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.consignee_name}
                onChange={(e) => setFormData({ ...formData, consignee_name: e.target.value })}
                placeholder="Recipient name"
                data-testid="consignee-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input
                value={formData.consignee_phone}
                onChange={(e) => setFormData({ ...formData, consignee_phone: e.target.value })}
                placeholder="10-digit phone"
                maxLength={10}
                data-testid="consignee-phone"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Email (Optional)</Label>
            <Input
              type="email"
              value={formData.consignee_email}
              onChange={(e) => setFormData({ ...formData, consignee_email: e.target.value })}
              placeholder="email@example.com"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Address *</Label>
            <Textarea
              value={formData.consignee_address}
              onChange={(e) => setFormData({ ...formData, consignee_address: e.target.value })}
              placeholder="Full delivery address"
              rows={2}
              data-testid="consignee-address"
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>City *</Label>
              <Input
                value={formData.consignee_city}
                onChange={(e) => setFormData({ ...formData, consignee_city: e.target.value })}
                placeholder="City"
              />
            </div>
            <div className="space-y-2">
              <Label>State *</Label>
              <Input
                value={formData.consignee_state}
                onChange={(e) => setFormData({ ...formData, consignee_state: e.target.value })}
                placeholder="State"
              />
            </div>
            <div className="space-y-2">
              <Label>Pincode</Label>
              <Input
                value={formData.consignee_pincode}
                disabled
                className="bg-gray-50"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Package Details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Box className="h-4 w-4" />
            Package Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Product Description *</Label>
            <Input
              value={formData.product_description}
              onChange={(e) => setFormData({ ...formData, product_description: e.target.value })}
              placeholder="Brief description of contents"
              data-testid="product-description"
            />
          </div>
          
          {/* iter-232.G — Two-row layout: Weight on its own row above,
              L/W/H share row 2. Fixes the mobile UX where the weight
              field was crammed into a 4-column grid and values like
              4.50 kg were partially clipped. */}
          <div className="space-y-3">
            <div className="space-y-2" data-testid="package-weight-row">
              <Label>Weight *</Label>
              <div className="flex gap-1.5 items-stretch">
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  placeholder={weightUnit === 'g' ? '500' : '0.5'}
                  className="flex-1 text-base"
                  data-testid="package-weight"
                />
                <select
                  value={weightUnit}
                  onChange={(e) => setWeightUnit(e.target.value)}
                  className="w-20 rounded-md border border-input bg-background px-2 text-sm"
                  data-testid="weight-unit-select"
                >
                  {WEIGHT_UNITS.map(u => (
                    <option key={u.value} value={u.value}>{u.value}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-muted-foreground">{getWeightInKg().toFixed(2)} kg</p>
            </div>
            <div className="grid grid-cols-3 gap-3" data-testid="package-dimensions-row">
              <div className="space-y-2">
                <Label>Length (cm)</Label>
                <Input
                  type="number"
                  value={formData.length}
                  onChange={(e) => setFormData({ ...formData, length: e.target.value })}
                  placeholder="16"
                  data-testid="package-length"
                />
              </div>
              <div className="space-y-2">
                <Label>Width (cm)</Label>
                <Input
                  type="number"
                  value={formData.width}
                  onChange={(e) => setFormData({ ...formData, width: e.target.value })}
                  placeholder="12"
                  data-testid="package-width"
                />
              </div>
              <div className="space-y-2">
                <Label>Height (cm)</Label>
                <Input
                  type="number"
                  value={formData.height}
                  onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                  placeholder="10"
                  data-testid="package-height"
                />
              </div>
            </div>
          </div>

          {/* Volumetric Budget Meter — warns when volumetric weight exceeds dead weight */}
          <VolumetricBudgetMeter
            length={formData.length}
            width={formData.width}
            height={formData.height}
            weight={getWeightInKg()}
            mode="fulfilment"
            dataTestIdPrefix="admin-delhivery-volumetric"
          />
        </CardContent>
      </Card>
      
      {/* Payment Details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <IndianRupee className="h-4 w-4" />
            Payment Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Payment Mode *</Label>
              <Select
                value={formData.payment_mode}
                onValueChange={(value) => setFormData({ ...formData, payment_mode: value })}
              >
                <SelectTrigger data-testid="payment-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Prepaid">Prepaid</SelectItem>
                  {serviceability?.cod && <SelectItem value="COD">Cash on Delivery</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Order Value (₹) *</Label>
              <Input
                type="number"
                value={formData.order_value}
                onChange={(e) => setFormData({ ...formData, order_value: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>
          
          {formData.payment_mode === 'COD' && (
            <div className="space-y-2">
              <Label>COD Amount (₹) *</Label>
              <Input
                type="number"
                value={formData.cod_amount}
                onChange={(e) => setFormData({ ...formData, cod_amount: e.target.value })}
                placeholder="Amount to collect"
              />
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Insurance Toggle */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-indigo-500" />
              <div>
                <p className="text-sm font-medium">Secure this Shipment</p>
                <p className="text-xs text-muted-foreground">Transit insurance for ₹{Math.max(Math.ceil((parseFloat(formData.order_value) || 0) * 0.01), 10)} (1% of order value)</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="insurance-toggle">
              <input
                type="checkbox"
                checked={insuranceOptIn}
                onChange={(e) => setInsuranceOptIn(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-500"></div>
            </label>
          </div>
        </CardContent>
      </Card>
      
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(1)}>
          Back
        </Button>
        <Button onClick={calculateRate} disabled={loading} data-testid="calculate-rate-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Truck className="h-4 w-4 mr-2" />}
          Calculate Shipping Rate
        </Button>
      </div>
    </div>
  );

  // Render Step 3: Confirm & Ship
  const renderStep3 = () => (
    <div className="space-y-6">
      {!shipmentResult ? (
        <>
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold">Review & Confirm Shipment</h3>
            <p className="text-gray-500 text-sm">Please review the details before creating the shipment</p>
          </div>
          
          {/* Rate Summary */}
          {rateInfo && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700">Estimated Shipping Cost</p>
                    <p className="text-2xl font-bold text-blue-900">
                      ₹{rateInfo.charges?.total_amount?.toFixed(2) || 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-blue-700">Billable Weight</p>
                    <p className="font-medium text-blue-900">{rateInfo.billable_weight} kg</p>
                  </div>
                </div>
                {rateInfo.estimated_delivery_days && (
                  <p className="text-sm text-blue-600 mt-2">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Estimated delivery: {rateInfo.estimated_delivery_days}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Shipment Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Shipment Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Recipient</p>
                  <p className="font-medium">{formData.consignee_name}</p>
                  <p className="text-gray-600">{formData.consignee_phone}</p>
                </div>
                <div>
                  <p className="text-gray-500">Delivery Address</p>
                  <p className="font-medium">{formData.consignee_address}</p>
                  <p className="text-gray-600">
                    {formData.consignee_city}, {formData.consignee_state} - {formData.consignee_pincode}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Package</p>
                  <p className="font-medium">{formData.weight} {weightUnit} ({getWeightInKg().toFixed(2)} kg)</p>
                  <p className="text-gray-600">{formData.length}×{formData.width}×{formData.height} cm</p>
                </div>
                <div>
                  <p className="text-gray-500">Payment</p>
                  <p className="font-medium">{formData.payment_mode}</p>
                  <p className="text-gray-600">Order Value: ₹{formData.order_value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}>
              Back to Edit
            </Button>
            <Button 
              onClick={createShipment} 
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="create-shipment-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Truck className="h-4 w-4 mr-2" />}
              Create Shipment with Delhivery
            </Button>
          </div>
        </>
      ) : (
        /* Success State */
        <div className="text-center space-y-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-green-700">Shipment Created Successfully!</h3>
            <p className="text-gray-500 mt-1">Your order is ready for pickup by Delhivery</p>
          </div>
          
          <Card className="bg-gray-50">
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 gap-4 text-left">
                <div>
                  <p className="text-sm text-gray-500">AWB Number</p>
                  <p className="font-mono font-bold text-lg">{shipmentResult.awb}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Order ID</p>
                  <p className="font-medium">{shipmentResult.order_id}</p>
                </div>
              </div>
              
              {shipmentResult.tracking_url && (
                <div className="mt-4 pt-4 border-t">
                  <a 
                    href={shipmentResult.tracking_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Track on Delhivery
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
          
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={downloadLabel}>
              <Download className="h-4 w-4 mr-2" />
              Download Label
            </Button>
            <Button onClick={onClose || (() => navigate('/orders'))}>
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4">
      {/* Progress Steps */}
      {!shipmentResult && (
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>1</div>
            <div className={`w-16 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>2</div>
            <div className={`w-16 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>3</div>
          </div>
        </div>
      )}
      
      {/* Step Content */}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </div>
  );
};

export default DelhiveryShipping;
